import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import enUS from "date-fns/locale/en-US";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
} from "date-fns";
import {
  RiCalendarLine,
  RiArrowRightDoubleLine,
  RiArrowLeftDoubleLine,
} from "@remixicon/react";

import { FulfillmentEventT } from "../../types/Fulfillment";
import { transformFulfillmentDataToEvents } from "../../utils/scheduleUtils";

import useGaneshApi from "../../hooks/useGaneshApi";
import ScheduleMenu from "../../components/Schedule/Menu";
import ScheduleSummaryEvent from "../../components/Schedule/ScheduleSummaryEvent";
import { useQuery } from "@tanstack/react-query";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function ScheduleSummaryPage() {
  const { date_id } = useParams();
  const primaryTextColor = useTheme().palette.text.primary;
  const navigate = useNavigate();
  const { apiRequest } = useGaneshApi();

  const currentDate = parse(date_id || "", "yyyy-MM", new Date());
  const date = startOfMonth(currentDate);

  const firstDayOfMonth = format(date, "yyyy-MM-dd");
  const lastDayOfMonth = format(endOfMonth(date), "yyyy-MM-dd");

  const { data, error, isLoading } = useQuery({
    queryKey: ["schedule", firstDayOfMonth],
    queryFn: () =>
      apiRequest<any[]>(
        `/v1/fulfillments_schedules/summary_counts?dateStart=${firstDayOfMonth}&dateEnd=${lastDayOfMonth}`,
        { method: "GET" }
      ),
    retry: 5,
  });

  const summaryEvents: FulfillmentEventT[] = transformFulfillmentDataToEvents(
    data?.data ?? []
  );

  const pageTitle = React.useMemo(
    () => ` {format(date, "MMMM yyyy")} Schedule Management`,
    [currentDate]
  );

  useEffect(() => {
    document.title = `Schedule Management: ${format(currentDate, "MMMM yyyy")}`;
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    document.title = `Schedule Management: ${format(currentDate, "MMMM yyyy")}`;
  }, [date_id]);

  const handleDayClick = (date: Date) => {
    const dateId = format(date, "yyyy-MM-dd");
    navigate(`/schedule/date/${dateId}`);
  };

  const handleNavigate = (action: "PREV" | "NEXT" | "TODAY") => {
    let newDate: Date;
    switch (action) {
      case "PREV":
        newDate = subMonths(date, 1);
        break;
      case "NEXT":
        newDate = addMonths(date, 1);
        break;
      case "TODAY":
        newDate = new Date();
        break;
    }

    navigate(
      `/schedule/summary/${
        action !== "TODAY" ? format(newDate, "yyyy-MM") : ""
      }`
    );
  };

  return (
    <Box sx={{ height: "100vh", p: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "start",
          mb: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {format(date, "MMMM yyyy")} Schedule Management
            </Typography>
            {!isLoading && error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error.message}
              </Typography>
            )}
          </Box>
          <Box>
            <ScheduleMenu />
          </Box>
        </Box>
      </Box>
      <Calendar
        date={date}
        defaultView="month"
        localizer={localizer}
        events={summaryEvents}
        startAccessor="start"
        endAccessor="start"
        style={{ height: "90%" }}
        selectable={true}
        views={["month"]}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: "transparent",
            color: primaryTextColor,
          },
        })}
        components={{
          event: ({ event }) => <ScheduleSummaryEvent event={event} />,
          toolbar: () => (
            <Box
              display="flex"
              justifyContent="end"
              alignItems="center"
              gap={1}
              mb={2}
            >
              {isLoading && (
                <CircularProgress
                  size={20}
                  sx={{ marginRight: 2, verticalAlign: "middle" }}
                />
              )}
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={() => handleNavigate("PREV")}
              >
                <RiArrowLeftDoubleLine />
              </Button>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={() => handleNavigate("TODAY")}
              >
                <RiCalendarLine size={16} style={{ marginRight: "4px" }} />{" "}
                Today
              </Button>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={() => handleNavigate("NEXT")}
              >
                <RiArrowRightDoubleLine />
              </Button>
            </Box>
          ),
        }}
        onSelectEvent={(event) => handleDayClick(event.start)}
        onSelectSlot={(slotInfo) => handleDayClick(slotInfo.start)}
        onNavigate={(newDate) =>
          handleNavigate(newDate > date ? "NEXT" : "PREV")
        }
      />
    </Box>
  );
}
